import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
  Star as StarIcon,
  SupervisorAccount as SupervisorIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';

// 🆕 IMPORTAR LOS NUEVOS COMPONENTES
import GestionTrabajadoresDialog from '../components/GestionTrabajadoresDialog';
import GestionServiciosDialog from '../components/GestionServiciosDialog';
import CamposDinamicosRenderer from '../components/CamposDinamicosRenderer';

// 🆕 COMPONENTES DE DIRECCIONES
import DireccionInput from '../components/forms/DireccionInput';
import DestinosMultiples from '../components/forms/DestinosMultiples';
import { useEmpresa } from '../context/EmpresaContext';

// ✅ ESTADOS CORREGIDOS - Usar números para consistencia
const estadosNoEditables = [5, 7, 8, 9, 10];
const estadosModificacionPermitida = [1, 2]; // Solo Pendiente(1) y Asignado(2)
const estadosAsignacionPermitida = [1, 2]; // Solo Pendiente(1) y Asignado(2)

const formatLocalDateTime = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Componente de Step personalizado
const CustomStepIcon = ({ active, completed, icon }) => {
  const icons = {
    1: <AssignmentIcon />,
    2: <WorkIcon />,
    3: <GroupIcon />
  };

  return (
    <Box
      sx={{
        width: 50,
        height: 50,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: completed 
          ? 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)'
          : active 
          ? 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)'
          : '#E0E0E0',
        color: completed || active ? 'white' : '#999',
        transition: 'all 0.3s ease'
      }}
    >
      {completed ? <CheckCircleIcon /> : icons[icon]}
    </Box>
  );
};

function OrdenServicioForm() {
  // ✅ ESTADO INICIAL CORREGIDO
  const [orden, setOrden] = useState({
    id_cliente: '',
    id_periodo: '',
    id_local: '',
    id_tiposervicio: 1,
    fecha_orden: formatLocalDateTime(),
    id_estado_servicio: 1  // ✅ NÚMERO en lugar de string
  });
  
  const [data, setData] = useState({
    clientes: [],
    tipos: [],
    periodos: [],
    locales: [],
    detalles: [],
    trabajadoresAsignados: [],
    tiposServicio: []  // 🆕 Tipos de servicio con configuracion de direcciones
  });

  // 🆕 Estados para direcciones
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [direccionOrigen, setDireccionOrigen] = useState({});
  const [destinos, setDestinos] = useState([]);

  const [dialogos, setDialogos] = useState({
    trabajadores: false,
    servicios: false,
    nuevoCliente: false
  });

  const [nuevoCliente, setNuevoCliente] = useState({
    nombre_cliente: '',
    apellido_cliente: '',
    tipo_documento: 'DNI',
    numero_documento: '',
    direccion_cliente: '',
    telefono_cliente: '',
    email_cliente: ''
  });

  const [error, setError] = useState('');
  const [errorCliente, setErrorCliente] = useState('');
  const [successCliente, setSuccessCliente] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [totalOrden, setTotalOrden] = useState(0);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const pasos = ['Crear Orden', 'Agregar Servicios', 'Asignar Personal'];
  const pasoActual = id ? (data.detalles.length > 0 ? 2 : 1) : 0;
  const isEditing = Boolean(id);

  // ✅ FUNCIONES DE VALIDACIÓN CORREGIDAS
  const isEditable = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    return !estadosNoEditables.includes(estadoActual);
  };

  const canAddModifyServices = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    return estadosModificacionPermitida.includes(estadoActual);
  };

  const canAssignWorkers = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    return estadosAsignacionPermitida.includes(estadoActual);
  };

  // ✅ FUNCIÓN DE ESTADO DESCRIPTIVO MEJORADA
  const getEstadoDescriptivo = () => {
    const estadoActual = parseInt(orden.id_estado_servicio);
    
    const estadosMap = {
      1: 'Pendiente',
      2: 'Asignado', 
      3: 'En Camino',
      4: 'En Proceso',
      5: 'Terminado',
      6: 'En Espera',
      7: 'Cancelado',
      8: 'Pausado',
      9: 'Rechazado',
      10: 'Completado'
    };
    
    return estadosMap[estadoActual] || orden.nombre_estado_servicio || 'Desconocido';
  };

  // ✅ FUNCIÓN getEstadoColor MEJORADA
  const getEstadoColor = (estado) => {
    let estadoTexto = estado;
    if (typeof estado === 'number') {
      const estadosMap = {
        1: 'pendiente',
        2: 'asignado',
        3: 'en camino', 
        4: 'en proceso',
        5: 'terminado',
        6: 'en espera',
        7: 'cancelado'
      };
      estadoTexto = estadosMap[estado] || 'desconocido';
    }
    
    switch(estadoTexto?.toLowerCase()) {
      case 'pendiente': return 'warning';
      case 'asignado': return 'info';
      case 'en espera': return 'warning';
      case 'en camino': return 'info';
      case 'en proceso': return 'primary';
      case 'terminado': return 'success';
      case 'completado': return 'success';
      case 'cancelado': return 'error';
      case 'rechazado': return 'error';
      case 'pausado': return 'default';
      default: return 'default';
    }
  };

  // ✅ TODOS LOS useEffect JUNTOS AL PRINCIPIO
  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    const total = data.detalles.reduce((sum, detalle) => {
      return sum + (detalle.cantidad * detalle.precio);
    }, 0);
    setTotalOrden(total);
  }, [data.detalles]);

  // 🆕 Actualizar tipo seleccionado cuando cambia el tipo de servicio
  useEffect(() => {
    if (orden.id_tiposervicio && data.tiposServicio.length > 0) {
      const tipo = data.tiposServicio.find(t => t.id_tipo_servicio === orden.id_tiposervicio);
      setTipoSeleccionado(tipo);

      // Resetear destinos si el tipo no los permite
      if (!tipo?.permite_multiples_destinos) {
        setDestinos([]);
      }

      // 🆕 Cargar dirección del cliente automáticamente si el tipo requiere dirección
      if (tipo?.requiere_direccion && orden.id_cliente && !direccionOrigen.direccion) {
        const clienteSeleccionado = data.clientes.find(c => c.id_cliente === orden.id_cliente);
        if (clienteSeleccionado?.direccion_cliente) {
          setDireccionOrigen({
            direccion: clienteSeleccionado.direccion_cliente,
            latitud: clienteSeleccionado.latitud_cliente || null,
            longitud: clienteSeleccionado.longitud_cliente || null,
            referencia: '',
            nombre_contacto: `${clienteSeleccionado.nombre_cliente} ${clienteSeleccionado.apellido_cliente}`,
            telefono_contacto: clienteSeleccionado.telefono_cliente || ''
          });
        }
      }
    }
  }, [orden.id_tiposervicio, data.tiposServicio]);

  // ✅ DEBUG useEffect CORREGIDO
  useEffect(() => {
    if (orden.id_estado_servicio) {
      console.log('🔍 Debug Estados:', {
        estado_raw: orden.id_estado_servicio,
        estado_type: typeof orden.id_estado_servicio,
        estado_parsed: parseInt(orden.id_estado_servicio),
        puede_modificar_servicios: canAddModifyServices(),
        puede_asignar_trabajadores: canAssignWorkers(),
        es_editable: isEditable(),
        estado_descriptivo: getEstadoDescriptivo()
      });
    }
  }, [orden.id_estado_servicio]);

  // ✅ LOAD DATA CORREGIDO
  const loadData = async () => {
    setLoading(true);
    try {
      const [clientesRes, tiposRes, periodosRes, localesRes, tiposServicioRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/clientes`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/general/tiposervicio`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/periodos`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/locales`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        // 🆕 Cargar tipos de servicio con configuracion de direcciones
        axios.get(`${config.API_BASE_URL}/api/v1/tipos-servicio`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }).catch(() => ({ data: { data: [] } })) // Si falla, devolver array vacio
      ]);

      setData(prev => ({
        ...prev,
        clientes: clientesRes.data,
        tipos: tiposRes.data,
        periodos: periodosRes.data,
        locales: localesRes.data,
        tiposServicio: tiposServicioRes.data?.data || tiposServicioRes.data || []
      }));

      if (id) {
        const [ordenRes, detallesRes, trabajadoresRes, destinosRes] = await Promise.all([
          axios.get(`${config.API_BASE_URL}/api/ordenes/${id}`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          axios.get(`${config.API_BASE_URL}/api/detalle-ordenes/${id}`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          axios.get(`${config.API_BASE_URL}/api/ordenes/${id}/trabajadores`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          // 🆕 Cargar destinos de la orden
          axios.get(`${config.API_BASE_URL}/api/v1/ordenes/${id}/destinos`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }).catch(() => ({ data: { data: [] } }))
        ]);

        const ordenData = ordenRes.data;

        // ✅ ASEGURAR QUE EL ESTADO SEA NÚMERO
        setOrden({
          ...ordenData,
          fecha_orden: formatLocalDateTime(new Date(ordenData.fecha_orden)),
          id_estado_servicio: parseInt(ordenData.id_estado_servicio) // ✅ CONVERTIR A NÚMERO
        });

        // 🆕 Cargar direccion de origen
        if (ordenData.direccion_origen) {
          setDireccionOrigen({
            direccion: ordenData.direccion_origen,
            referencia: ordenData.referencia_origen,
            nombre_contacto: ordenData.contacto_origen,
            telefono_contacto: ordenData.telefono_origen,
            latitud: ordenData.latitud_origen,
            longitud: ordenData.longitud_origen
          });
        }

        // 🆕 Cargar destinos
        const destinosData = destinosRes.data?.data || destinosRes.data || [];
        setDestinos(destinosData);

        setData(prev => ({
          ...prev,
          detalles: detallesRes.data,
          trabajadoresAsignados: trabajadoresRes.data
        }));
      }
    } catch (error) {
      setError('Error al cargar los datos necesarios');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (!isEditable()) return;
    setOrden(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable()) return;

    setLoading(true);
    try {
      // 🆕 Preparar datos con direcciones
      const ordenData = {
        ...orden,
        // Direccion origen
        direccion_origen: direccionOrigen.direccion,
        latitud_origen: direccionOrigen.latitud,
        longitud_origen: direccionOrigen.longitud,
        referencia_origen: direccionOrigen.referencia,
        contacto_origen: direccionOrigen.nombre_contacto,
        telefono_origen: direccionOrigen.telefono_contacto,
        // Destinos
        destinos: destinos
      };

      if (id) {
        await axios.put(
          `${config.API_BASE_URL}/api/ordenes/${id}`,
          ordenData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setSuccess(true);
        setTimeout(() => loadData(), 1000);
      } else {
        const response = await axios.post(
          `${config.API_BASE_URL}/api/ordenes`,
          ordenData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setSuccess(true);
        setTimeout(() => {
          navigate(`/home/ordenes/editar/${response.data.id_orden_servicio}`);
        }, 1500);
      }
    } catch (error) {
      setError('Error al guardar la orden');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCancel = () => {
    navigate('/home/ordenes');
  };

  // Funciones para abrir diálogos de gestión
  const abrirGestionTrabajadores = () => {
    setDialogos({ ...dialogos, trabajadores: true });
  };

  const cerrarGestionTrabajadores = () => {
    setDialogos({ ...dialogos, trabajadores: false });
  };

  const abrirGestionServicios = () => {
    setDialogos({ ...dialogos, servicios: true });
  };

  const cerrarGestionServicios = () => {
    setDialogos({ ...dialogos, servicios: false });
  };

  const recargarDatos = () => {
    loadData();
  };

  // Funciones para manejar creación rápida de clientes
  const abrirDialogoNuevoCliente = () => {
    setDialogos({ ...dialogos, nuevoCliente: true });
  };

  const cerrarDialogoNuevoCliente = () => {
    setDialogos({ ...dialogos, nuevoCliente: false });
    setNuevoCliente({
      nombre_cliente: '',
      apellido_cliente: '',
      tipo_documento: 'DNI',
      numero_documento: '',
      direccion_cliente: '',
      telefono_cliente: '',
      email_cliente: ''
    });
    setErrorCliente('');
    setSuccessCliente(false);
  };

  const handleChangeNuevoCliente = (e) => {
    const { name, value } = e.target;
    setNuevoCliente(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCrearCliente = async () => {
    // Validar campos obligatorios
    if (!nuevoCliente.nombre_cliente || !nuevoCliente.apellido_cliente ||
        !nuevoCliente.numero_documento || !nuevoCliente.telefono_cliente) {
      setErrorCliente('Por favor complete todos los campos obligatorios');
      return;
    }

    setLoadingCliente(true);
    setErrorCliente('');

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/clientes`,
        nuevoCliente,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Mostrar mensaje de éxito
      setSuccessCliente(true);

      // Actualizar la lista de clientes
      const clientesRes = await axios.get(`${config.API_BASE_URL}/api/clientes`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setData(prev => ({ ...prev, clientes: clientesRes.data }));

      // Seleccionar el nuevo cliente automáticamente
      setOrden(prev => ({ ...prev, id_cliente: response.data.id_cliente }));

      // Cerrar el diálogo después de un breve delay
      setTimeout(() => {
        cerrarDialogoNuevoCliente();
      }, 1500);

    } catch (error) {
      setErrorCliente(error.response?.data?.error || 'Error al crear el cliente');
    } finally {
      setLoadingCliente(false);
    }
  };

  if (loading && isEditing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos de la orden...
        </Typography>
      </Box>
    );
  }

  // Encontrar el responsable
  const responsable = data.trabajadoresAsignados.find(t => t.es_responsable);

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header con botón volver */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{
              color: '#1A237E',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>
          
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              {isEditing ? `Editar Orden #${id}` : 'Nueva Orden de Servicio'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Gestión avanzada - Solo estados Pendiente y Asignado permiten modificaciones' : 'Crea una nueva orden de servicio'}
            </Typography>
          </Box>
        </Box>

        {orden.id_estado_servicio && isEditing && (
          <Chip
            label={getEstadoDescriptivo()}
            color={getEstadoColor(getEstadoDescriptivo())}
            sx={{ 
              fontWeight: 'bold',
              fontSize: '0.875rem',
              height: 32
            }}
          />
        )}
      </Box>

      {/* 🐛 DEBUGGING TEMPORAL - Remover después de verificar */}
      {isEditing && (
        <Paper sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: '#f5f5f5',
          border: '1px dashed #ccc'
        }}>
          <Typography variant="caption" component="div">
            <strong>🔍 DEBUG ESTADOS:</strong><br/>
            - Estado actual: {orden.id_estado_servicio} (tipo: {typeof orden.id_estado_servicio})<br/>
            - Estado descriptivo: {getEstadoDescriptivo()}<br/>
            - Puede modificar servicios: {canAddModifyServices() ? '✅' : '❌'}<br/>
            - Puede asignar trabajadores: {canAssignWorkers() ? '✅' : '❌'}<br/>
            - Es editable: {isEditable() ? '✅' : '❌'}
          </Typography>
        </Paper>
      )}

      {/* Información del responsable */}
      {responsable && (
        <Paper sx={{ 
          mb: 3, 
          p: 3, 
          borderRadius: 3,
          border: '1px solid rgba(255, 193, 7, 0.3)',
          background: 'linear-gradient(135deg, #FFF8E1 0%, #FFFDE7 100%)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#FFB300', color: 'white' }}>
              <SupervisorIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#E65100' }}>
                👑 Responsable del Servicio
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {responsable.nombre_trabajador} {responsable.apellido_trabajador}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Solo esta persona puede cambiar los estados del servicio
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Alerts */}
      {!canAddModifyServices() && isEditing && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<WarningIcon />}
        >
          <strong>Modificaciones no permitidas</strong><br/>
          Solo se pueden modificar servicios y trabajadores en estados: <strong>Pendiente (1)</strong> y <strong>Asignado (2)</strong>.<br/>
          Estado actual: <strong>{getEstadoDescriptivo()} ({orden.id_estado_servicio})</strong>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Orden {isEditing ? 'actualizada' : 'creada'} exitosamente.
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ 
        mb: 4, 
        p: 3, 
        borderRadius: 3,
        border: '1px solid rgba(26, 35, 126, 0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
      }}>
        <Stepper activeStep={pasoActual} alternativeLabel>
          {pasos.map((label, index) => (
            <Step key={label}>
              <StepLabel StepIconComponent={(props) => 
                <CustomStepIcon {...props} icon={index + 1} />
              }>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Formulario Principal */}
      <Paper sx={{ 
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid rgba(26, 35, 126, 0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        mb: 3
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          p: { xs: 3, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <AssignmentIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold' }}>
            Información de la Orden
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2 }}>
                  <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Información Básica
                </Typography>
              </Grid>

              {/* 🆕 Campo Tipo de Servicio */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Servicio *</InputLabel>
                  <Select
                    name="id_tiposervicio"
                    value={orden.id_tiposervicio}
                    onChange={handleChange}
                    required
                    disabled={!isEditable()}
                    sx={{ borderRadius: 2 }}
                  >
                    {data.tiposServicio.map((tipo) => (
                      <MenuItem key={tipo.id_tipo_servicio} value={tipo.id_tipo_servicio}>
                        {tipo.nombre_tipo}
                        {tipo.requiere_direccion && ' (Requiere direccion)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    options={data.clientes}
                    getOptionLabel={(option) => `${option.nombre_cliente} ${option.apellido_cliente} - ${option.numero_documento || 'Sin documento'}`}
                    value={data.clientes.find(c => c.id_cliente === orden.id_cliente) || null}
                    onChange={(event, newValue) => {
                      setOrden(prev => ({
                        ...prev,
                        id_cliente: newValue ? newValue.id_cliente : ''
                      }));

                      // 🆕 Cargar dirección del cliente si el tipo de servicio requiere dirección
                      if (newValue && tipoSeleccionado?.requiere_direccion && newValue.direccion_cliente) {
                        setDireccionOrigen({
                          direccion: newValue.direccion_cliente,
                          latitud: newValue.latitud_cliente || null,
                          longitud: newValue.longitud_cliente || null,
                          referencia: '',
                          nombre_contacto: `${newValue.nombre_cliente} ${newValue.apellido_cliente}`,
                          telefono_contacto: newValue.telefono_cliente || ''
                        });
                      }
                    }}
                    disabled={!isEditable()}
                    isOptionEqualToValue={(option, value) => option.id_cliente === value.id_cliente}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente *"
                        required
                        placeholder="Buscar por nombre, apellido o documento..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#4FC3F7' },
                            '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.nombre_cliente} {option.apellido_cliente}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.tipo_documento}: {option.numero_documento || 'No especificado'}
                            {option.telefono_cliente && ` • Tel: ${option.telefono_cliente}`}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText="No se encontraron clientes"
                    sx={{
                      '& .MuiAutocomplete-clearIndicator': {
                        color: '#1A237E'
                      }
                    }}
                  />
                  <Tooltip title="Crear nuevo cliente">
                    <Button
                      variant="contained"
                      onClick={abrirDialogoNuevoCliente}
                      disabled={!isEditable()}
                      sx={{
                        minWidth: 'auto',
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                        boxShadow: '0 4px 12px rgba(79, 195, 247, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)',
                          boxShadow: '0 6px 16px rgba(79, 195, 247, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': {
                          background: '#E0E0E0'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <PersonAddIcon />
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Local</InputLabel>
                  <Select
                    name="id_local"
                    value={orden.id_local}
                    onChange={handleChange}
                    required
                    disabled={!isEditable()}
                    sx={{ borderRadius: 2 }}
                  >
                    {data.locales.map((local) => (
                      <MenuItem key={local.id_local} value={local.id_local}>
                        {local.nombre_local}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Periodo</InputLabel>
                  <Select
                    name="id_periodo"
                    value={orden.id_periodo}
                    onChange={handleChange}
                    required
                    disabled={!isEditable()}
                    sx={{ borderRadius: 2 }}
                  >
                    {data.periodos.map((periodo) => (
                      <MenuItem key={periodo.id_periodo} value={periodo.id_periodo}>
                        {`${periodo.mes}/${periodo.anio}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fecha y Hora del Servicio"
                  type="datetime-local"
                  name="fecha_orden"
                  value={orden.fecha_orden}
                  onChange={handleChange}
                  disabled={!isEditable()}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              {/* 🆕 Seccion de Direcciones - Renderizado condicional segun tipo */}
              {tipoSeleccionado && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2 }}>
                      <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Direcciones del Servicio
                    </Typography>

                    {/* Direccion Origen - Si requiere_direccion */}
                    {tipoSeleccionado.requiere_direccion && (
                      <DireccionInput
                        label="Direccion de Origen / Cliente"
                        value={direccionOrigen}
                        onChange={setDireccionOrigen}
                        requiereCoordenadas={tipoSeleccionado.requiere_coordenadas}
                        disabled={!isEditable()}
                      />
                    )}

                    {/* Destino unico - Si requiere_destino pero NO multiples */}
                    {tipoSeleccionado.requiere_destino && !tipoSeleccionado.permite_multiples_destinos && (
                      <DireccionInput
                        label="Direccion de Destino"
                        value={destinos[0] || {}}
                        onChange={(data) => setDestinos([data])}
                        requiereCoordenadas={tipoSeleccionado.requiere_coordenadas}
                        disabled={!isEditable()}
                      />
                    )}

                    {/* Multiples destinos */}
                    {tipoSeleccionado.permite_multiples_destinos && (
                      <DestinosMultiples
                        destinos={destinos}
                        onChange={setDestinos}
                        maxDestinos={tipoSeleccionado.max_destinos}
                        requiereCoordenadas={tipoSeleccionado.requiere_coordenadas}
                        disabled={!isEditable()}
                      />
                    )}

                    {/* Mensaje si no requiere direcciones */}
                    {!tipoSeleccionado.requiere_direccion && (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Este tipo de servicio no requiere direccion (servicio fijo en local)
                      </Alert>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2, 
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(26, 35, 126, 0.1)'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: '#1A237E',
                      color: '#1A237E',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        borderColor: '#0D47A1',
                        backgroundColor: 'rgba(26, 35, 126, 0.04)'
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={!isEditable() || loading}
                    sx={{
                      background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                        boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: '#E0E0E0',
                        color: '#999'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Guardando...' : (id ? 'Actualizar Orden' : 'Crear Orden')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>

      {/* Sección de Servicios */}
      {id && (
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          mb: 3
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
            color: 'white',
            p: { xs: 3, sm: 4 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BuildIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
              <Box>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
                  Servicios ({data.detalles.length})
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total: {simboloMoneda} {totalOrden.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={abrirGestionServicios}
              disabled={!canAddModifyServices()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }
              }}
            >
              Gestionar Servicios
            </Button>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {data.detalles.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Servicio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio Unit.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.detalles.map((detalle) => (
                      <TableRow key={detalle.id_detalle} sx={{ '&:hover': { bgcolor: 'rgba(79, 195, 247, 0.08)' } }}>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {detalle.nombre_servicio}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{detalle.cantidad}</TableCell>
                        <TableCell align="right">{simboloMoneda} {parseFloat(detalle.precio).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {simboloMoneda} {(detalle.cantidad * detalle.precio).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'rgba(26, 35, 126, 0.04)' }}>
                      <TableCell colSpan={3} align="right">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                          {simboloMoneda} {totalOrden.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No hay servicios agregados. Usa "Gestionar Servicios" para agregar servicios.
              </Alert>
            )}
          </Box>
        </Paper>
      )}

      {/* Sección de Personal */}
      {id && (
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            color: 'white',
            p: { xs: 3, sm: 4 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <GroupIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
              <Box>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
                  Personal Asignado ({data.trabajadoresAsignados.length})
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Sistema avanzado con responsable
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<GroupIcon />}
              onClick={abrirGestionTrabajadores}
              disabled={!canAssignWorkers()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }
              }}
            >
              Gestionar Personal
            </Button>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {data.trabajadoresAsignados.length > 0 ? (
              <Grid container spacing={2}>
                {data.trabajadoresAsignados.map((trabajador) => (
                  <Grid item xs={12} md={6} key={trabajador.id_asignacion}>
                    <Card sx={{ 
                      borderRadius: 2, 
                      border: trabajador.es_responsable ? '2px solid #FFD700' : '1px solid rgba(26, 35, 126, 0.1)',
                      '&:hover': { 
                        boxShadow: '0 4px 12px rgba(26, 35, 126, 0.15)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}>
                      {trabajador.es_responsable && (
                        <Box sx={{
                          position: 'absolute',
                          top: -8,
                          right: 8,
                          bgcolor: '#FFD700',
                          borderRadius: '50%',
                          p: 1,
                          zIndex: 1
                        }}>
                          <StarIcon sx={{ fontSize: 16, color: '#B8860B' }} />
                        </Box>
                      )}
                      
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#1A237E', width: 48, height: 48 }}>
                            {trabajador.nombre_trabajador?.[0]}{trabajador.apellido_trabajador?.[0]}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {trabajador.es_responsable && (
                                <Chip 
                                  label="Responsable" 
                                  size="small" 
                                  color="warning"
                                  icon={<SupervisorIcon />}
                                />
                              )}
                              <Chip
                                label={trabajador.descripcion_estado}
                                color={getEstadoColor(trabajador.descripcion_estado)}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Comisión
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {trabajador.comision}%
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Precio Base
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {simboloMoneda} {parseFloat(trabajador.precio_servicio || 0).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Comisión Total: <strong>{simboloMoneda} {((trabajador.precio_servicio * trabajador.comision) / 100).toFixed(2)}</strong>
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No hay personal asignado. Usa "Gestionar Personal" para asignar trabajadores.
              </Alert>
            )}
          </Box>
        </Paper>
      )}

      {/* Campos Dinámicos del Estado */}
      {id && orden.id_estado_servicio && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <CamposDinamicosRenderer
            idOrden={id}
            idEstado={orden.id_estado_servicio}
            soloLectura={!isEditable()}
            onValoresChange={(valores) => {
              console.log('Valores campos dinámicos:', valores);
            }}
          />
        </Paper>
      )}

      {/* Diálogos de gestión */}
      <GestionTrabajadoresDialog
        open={dialogos.trabajadores}
        onClose={cerrarGestionTrabajadores}
        ordenId={id}
        ordenEstado={orden.id_estado_servicio}
        trabajadoresAsignados={data.trabajadoresAsignados}
        serviciosActuales={data.detalles} 
        onTrabajadoresUpdated={recargarDatos}
      />

      <GestionServiciosDialog
        open={dialogos.servicios}
        onClose={cerrarGestionServicios}
        ordenId={id}
        ordenEstado={orden.id_estado_servicio}
        serviciosActuales={data.detalles}
        onServiciosUpdated={recargarDatos}
      />

      {/* Botones de navegación finales */}
      <Box sx={{ mt: 4, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/home/ordenes')}
          sx={{
            borderColor: '#1A237E',
            color: '#1A237E',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          Volver a la lista
        </Button>
        
        {id && !canAddModifyServices() && (
          <Alert severity="info" sx={{ flex: 1, ml: 2, borderRadius: 2 }}>
            Solo se pueden hacer modificaciones en estados <strong>Pendiente</strong> y <strong>Asignado</strong>
          </Alert>
        )}
      </Box>

      {/* Diálogo para Crear Nuevo Cliente */}
      <Dialog
        open={dialogos.nuevoCliente}
        onClose={cerrarDialogoNuevoCliente}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3
        }}>
          <PersonAddIcon sx={{ fontSize: '2rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Crear Nuevo Cliente
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pt: 4, pb: 2 }}>
          {/* Mensajes de error y éxito dentro del modal */}
          {errorCliente && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errorCliente}
            </Alert>
          )}

          {successCliente && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              Cliente creado exitosamente
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre *"
                name="nombre_cliente"
                value={nuevoCliente.nombre_cliente}
                onChange={handleChangeNuevoCliente}
                required
                error={!nuevoCliente.nombre_cliente && errorCliente !== ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#4FC3F7' },
                    '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido *"
                name="apellido_cliente"
                value={nuevoCliente.apellido_cliente}
                onChange={handleChangeNuevoCliente}
                required
                error={!nuevoCliente.apellido_cliente && errorCliente !== ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#4FC3F7' },
                    '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo Documento *</InputLabel>
                <Select
                  name="tipo_documento"
                  value={nuevoCliente.tipo_documento}
                  onChange={handleChangeNuevoCliente}
                  required
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="DNI">DNI</MenuItem>
                  <MenuItem value="RUC">RUC</MenuItem>
                  <MenuItem value="CE">CE</MenuItem>
                  <MenuItem value="Pasaporte">Pasaporte</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Número de Documento *"
                name="numero_documento"
                value={nuevoCliente.numero_documento}
                onChange={handleChangeNuevoCliente}
                required
                error={!nuevoCliente.numero_documento && errorCliente !== ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#4FC3F7' },
                    '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono *"
                name="telefono_cliente"
                value={nuevoCliente.telefono_cliente}
                onChange={handleChangeNuevoCliente}
                required
                error={!nuevoCliente.telefono_cliente && errorCliente !== ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#4FC3F7' },
                    '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Opcional)"
                name="email_cliente"
                type="email"
                value={nuevoCliente.email_cliente}
                onChange={handleChangeNuevoCliente}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#4FC3F7' },
                    '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección (Opcional)"
                name="direccion_cliente"
                value={nuevoCliente.direccion_cliente}
                onChange={handleChangeNuevoCliente}
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#4FC3F7' },
                    '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, gap: 2, borderTop: '1px solid rgba(26, 35, 126, 0.1)' }}>
          <Button
            onClick={cerrarDialogoNuevoCliente}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{
              borderColor: '#1A237E',
              color: '#1A237E',
              borderRadius: 2,
              px: 3,
              '&:hover': {
                borderColor: '#0D47A1',
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCrearCliente}
            variant="contained"
            startIcon={loadingCliente ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={
              loadingCliente ||
              !nuevoCliente.nombre_cliente ||
              !nuevoCliente.apellido_cliente ||
              !nuevoCliente.numero_documento ||
              !nuevoCliente.telefono_cliente
            }
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: '#E0E0E0',
                color: '#999'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {loadingCliente ? 'Creando...' : 'Crear Cliente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OrdenServicioForm;